import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  _id: string
  username: string
  email?: string
  full_name?: string
  phone_number?: string
  country_code?: string
  preferred_language: string
  content_language?: string
  response_level: string
  wallet_balance: number
  is_admin: boolean
  is_google_user: boolean
  picture?: string
  profile_picture?: string
  created_at: string
  has_password?: boolean
  // Verification & Security
  two_factor_enabled?: boolean
  two_factor_method?: 'email' | 'phone'
  email_verified?: boolean
  phone_verified?: boolean
  // Auth providers
  auth_providers?: string[]  // ['google', 'email', 'phone']
  primary_auth_method?: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  setUser: (user: User | null) => void
  setLoading: (isLoading: boolean) => void
  login: (user: User, token: string) => void
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  updateBalance: (newBalance: number) => void
  deductBalance: (amount: number) => void
  addBalance: (amount: number) => void
}

const normalizeUser = (user: User | null): User | null => {
  if (!user) return null

  const rawUser = user as User & { id?: string }
  const normalizedId = rawUser._id || rawUser.id || ''

  return {
    ...rawUser,
    _id: normalizedId,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => {
        const normalizedUser = normalizeUser(user)
        set({
          user: normalizedUser,
          isAuthenticated: !!normalizedUser,
        })
      },

      setLoading: (isLoading) => set({ isLoading }),

      login: (user, token) => {
        const normalizedUser = normalizeUser(user)
        set({
          user: normalizedUser,
          token,
          isAuthenticated: !!normalizedUser,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updateBalance: (newBalance) =>
        set((state) => ({
          user: state.user ? { ...state.user, wallet_balance: newBalance } : null,
        })),

      deductBalance: (amount) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, wallet_balance: Math.max(0, state.user.wallet_balance - amount) }
            : null,
        })),

      addBalance: (amount) =>
        set((state) => ({
          user: state.user
            ? { ...state.user, wallet_balance: state.user.wallet_balance + amount }
            : null,
        })),
    }),
    {
      name: 'auth-storage',
      // Only persist non-sensitive user data
      partialize: (state) => ({
        user: state.user ? {
          _id: state.user._id,
          username: state.user.username,
          email: state.user.email,
          full_name: state.user.full_name,
          phone_number: state.user.phone_number,
          country_code: state.user.country_code,
          preferred_language: state.user.preferred_language,
          content_language: state.user.content_language,
          response_level: state.user.response_level,
          wallet_balance: state.user.wallet_balance,
          is_google_user: state.user.is_google_user,
          picture: state.user.picture,
          profile_picture: state.user.profile_picture,
          created_at: state.user.created_at,
          has_password: state.user.has_password,
          two_factor_enabled: state.user.two_factor_enabled,
          two_factor_method: state.user.two_factor_method,
          email_verified: state.user.email_verified,
          phone_verified: state.user.phone_verified,
          auth_providers: state.user.auth_providers,
          primary_auth_method: state.user.primary_auth_method,
        } : null,
        isAuthenticated: state.isAuthenticated,
        token: state.token,
      }),
    }
  )
)
